import store from '../store'
import qs from 'qs'

import Vue from 'vue'
import Router from 'vue-router'

import ApiClient from '@/components/api-client'

import BrandRoutes from '@/router/brand'

import { forEach, toPairs } from 'lodash'

Vue.use(Router)

const publicRoutes = [
  'login',
  'public-sources',
  'privacy-policy',
  'email-unsubscribe',
  'forgot-password',
  'reset-password',
  'maintenance'
]

const signedInRedirect = function (to, from, next) {
  const user = store.state.user
  if (!user.isAnonymous) {
    if (to.query.n !== undefined) {
      return next({path: decodeURIComponent(to.query.n)})
    }
    return next({name: 'home'})
  }
  next()
}

const router = new Router({
  mode: 'history',
  routes: [
    {
      path: '/login',
      name: 'login',
      component: () => import(/* webpackChunkName */ '@/components/Authentication/Login'),
      beforeEnter: signedInRedirect
    },
    {
      path: '/maintenance',
      name: 'maintenance',
      component: () => import(/* webpackChunkName: "maintenance" */ '@/components/Maintenance')
    },
    {
      path: '/logout',
      name: 'logout',
      component: () => import(/* webpackChunkName: "logout" */ '@/components/Authentication/Logout')
    },
    {
      path: '/forgot-password',
      name: 'forgot-password',
      component: () => import(/* webpackChunkName: "forgotPassword" */ '@/components/Authentication/ForgotPassword'),
      beforeEnter: signedInRedirect
    },
    {
      path: '/reset-password',
      name: 'reset-password',
      component: () => import(/* webpackChunkName: "resetPassword" */ '@/components/Authentication/ResetPassword'),
      beforeEnter: signedInRedirect
    },
    {
      path: '/privacy-policy',
      name: 'privacy-policy',
      component: () => import(/* webpackChunkName: "privacyPolicy" */ '@/components/PrivacyPolicy')
    },
    {
      path: '/privacy-policy-acceptance',
      name: 'privacy-policy-acceptance',
      component: () => import(/* webpackChunkName: "privacyPolicyAcceptance" */ '@/components/PrivacyPolicyAcceptance')
    },
    {
      path: '/',
      component: () => import(/* webpackChunkName: "CQMain" */ '@/components/CQMain'),
      children: [
        {
          path: '/',
          name: 'home',
          component: () => import(/* webpackChunkName: "home" */ '@/components/Home')
        },
        {
          path: '/:brandName',
          component: () => import(/* webpackChunkName: "brand" */ '@/components/Brand'),
          props: true,
          beforeEnter: (to, from, next) => {
            store.commit('setBrand', {})
            store.dispatch('loadBrand', to.params.brandName)
              .then(
                () => {
                  next()
                }
              )
              .catch(
                (error) => {
                  const status = error.response.status
                  switch (status) {
                    case 403:
                    case 404:
                      next({
                        name: 'notFound',
                        params: [to.path]
                      })
                      break
                    case 500:
                      next({
                        name: 'notFound',
                        params: [to.path]
                      })
                      break
                    default:
                      break
                  }
                }
              )
          },
          children: [
            ...BrandRoutes
          ]
        }
      ]
    },
    {
      path: '*',
      name: 'notFound',
      component: () => import(/* webpackChunkName: "notFound" */ '@/components/NotFound')
    }
  ],
  // set custom query resolver
  parseQuery (query) {
    return qs.parse(query, { strictNullHandling: true })
  },
  stringifyQuery (query) {
    var result = qs.stringify(query, { strictNullHandling: true })

    return result ? ('?' + result) : ''
  },
  scrollBehavior (to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else {
      return {x: 0, y: 0}
    }
  }
})

router.beforeEach((to, from, next) => {
  if (to.name === 'notFound') {
    return next()
  }

  if (to.query.t !== undefined) {
    let qs = '?'
    forEach(toPairs(to.query), (val) => {
      if (val[0] !== 't') {
        qs = qs + `&${val[0]}=${val[1]}`
      }
    })
    ApiClient.magicLogin(to.query.t).then(() => {
      window.location.href = to.path + qs
    }).catch(() => {
      return next({name: 'login', query: {n: encodeURIComponent(to.path + qs)}})
    })
  } else {
    store.dispatch('loadUser')
      .then(
        user => {
          if (to.query.emailClick !== undefined) {
            delete to.query.emailClick
          }

          if (publicRoutes.includes(to.name)) {
            return next()
          }

          if (user.settings.maintenanceMode === true) {
            return next({name: 'maintenance'})
          }

          if (user.settings.maintenanceMode === false && to.name === 'maintenance') {
            return next({name: 'login'})
          }

          if (user.isAnonymous) {
            if (to.fullPath === '/') {
              next({name: 'login'})
            } else {
              next({
                name: 'login',
                query: {
                  n: encodeURIComponent(to.fullPath)
                }
              })
            }
          }

          if (!user.isAnonymous && !user.acceptedPrivacyPolicy && !user.isImpersonated) {
            if (to.name === 'privacy-policy-acceptance') {
              next()
            } else {
              next({name: 'privacy-policy-acceptance'})
            }
          }

          if (to.name === 'privacy-policy-acceptance' && !user.isAnonymous && user.acceptedPrivacyPolicy) {
            next({name: 'home'})
          }

          for (const route of to.matched) {
            if (route.meta.requires) {
              if (!user.roles.includes(route.meta.requires)) {
                router.replace({name: 'notFound', params: [to.path]})
              }
            }
          }
          next()
        }
      ).catch(
      () => {
        store.dispatch('logOutUser')
        next({
          name: 'login'
        })
      }
    )
  }
})

export default router
